import helpers from './helpers';
import authCrypto from './authCrypto';
import axios from 'axios';

function _parseUrlParams(str) {
  var pieces = str.split('&'), data = {}, i, parts;
  // process each query pair
  for (i = 0; i < pieces.length; i++) {
    parts = pieces[i].split('=');
    if (parts.length < 2) {
      parts.push('');
    }
    data[decodeURIComponent(parts[0])] = decodeURIComponent(parts[1]);
  }
  return data;
}

async function _getPkceTokens(code, verifier, tokenUri, client_id, redirectUri, clientSecret) {
  // appropriated from getTokens() https://bitbucket.det.nsw.edu.au/projects/AM/repos/doe-examples/browse/oauthPKCEAuthorizationGrantExample/helpers.js
  const data = {
    'code': code,
    'grant_type': 'authorization_code',
    'client_id': client_id,
    'client_secret': clientSecret || '',
    'code_verifier': verifier,
    'redirect_uri': redirectUri
  };

  const formBody = [];
  for (const property in data) {
    formBody.push(helpers.encodeUriComponent(property) + '=' + helpers.encodeUriComponent(data[property]));
  }

  const formBodyStr = formBody.join('&');
  const fetchOptions = {
    url: tokenUri,
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Accept': 'application/json'
    },
    data: formBodyStr
  };

  const response = await axios(fetchOptions);
  return response.data;
}

export default {
  attachOAuthRoutes(router, redirectUri) {
    let callbackPath;
    try {
      const callbackRoute = new URL(redirectUri);
      callbackPath = callbackRoute.pathname;
    } catch {
      callbackPath = '/login/callback';
    }

    // attach login callback and navigation guard to router
    const loginCallbackRoute = {
      path: callbackPath,
      name: 'loginCallback',
      meta: {
        protected: false
      },
      beforeEnter: async (to, from, next) => {
        let tokens, parsedIdToken;

        const ourNonce = sessionStorage.getItem('nonce');
        sessionStorage.removeItem('nonce');

        if (router.app.$OAuth.pkce) {
          // authorization grant pkce
          const codeVerifier = sessionStorage.getItem('codeVerifier');
          sessionStorage.removeItem('codeVerifier');
          const callbackParams = to.query;

          // we have to make an additional ajax call to get the tokens with the auth code
          const tokenUri = router.app.$OAuth.tokenUri || `${callbackParams.iss}/access_token`;
          if (callbackParams.code) {
            tokens = await _getPkceTokens(callbackParams.code, codeVerifier, tokenUri, router.app.$OAuth.params.client_id, router.app.$OAuth.params.redirect_uri, router.app.$OAuth.clientSecret);
            parsedIdToken = helpers.parseJwt(tokens.id_token);
          }
        } else {
          // implicit auth
          // remove a # prefix
          const hashParamsStr = to.hash.replace(/^#?(.*)/, '$1');

          // we get the tokens directly in the hash of the url
          tokens = _parseUrlParams(hashParamsStr);
          parsedIdToken = helpers.parseJwt(tokens.id_token);
        }

        if (parsedIdToken && await authCrypto.verifyHash(ourNonce, parsedIdToken.nonce)) {
          router.app.$OAuth.setToken(tokens.access_token, tokens.id_token, tokens.token_type, tokens.refresh_token);
          const nextPath = sessionStorage.getItem('oauth-redirect-after-callback') || '/';
          sessionStorage.removeItem('oauth-redirect-after-callback')
          next(nextPath);
        } else {
          if (typeof router.app.$OAuth.afterLogin === 'function') {
            router.app.$OAuth.afterLogin();
          }
          throw('Authorisation not valid. Try again.');
        }
      }
    };

    router.addRoutes([loginCallbackRoute]);

    router.beforeEach(async (to, from, next) => {
      // call isAuthenticated to load any data from local storage
      const isAuthenticated = router.app.$OAuth.isAuthenticated();
      const secureApp = router.app.$OAuth.secureApp;

      if ((secureApp || (to.meta && to.meta.protected)) && to.name !== 'loginCallback') {
        if (!isAuthenticated) {
          sessionStorage.setItem('oauth-redirect-after-callback', to.fullPath);
          if (!await router.app.$OAuth.getAuth()) {
            // could not get valid token without redirection. Deny route now and navigate again after callback.
            return next(false);
          }
        }
      }
      next();
    });
  }
}
